/**
 * Plate Carrier Konfigurator
 * Alpine.js + Konva.js product configurator for Shopify
 *
 * Expects window.__configuratorData to be set by the Liquid section.
 */

document.addEventListener('alpine:init', () => {
  const data = window.__configuratorData || {};
  const settings = data.settings || {};
  const colors = data.colors || [];
  const sizes = data.sizes || [];
  const zones = data.zones || [];
  const baseImages = data.baseImages || [];
  const attachments = data.attachments || [];
  const presets = data.presets || [];
  const moneyFormat = data.moneyFormat || '{{amount_with_comma_separator}} €';
  const cartAddUrl = data.cartAddUrl || '/cart/add.js';

  // Color alias groups — names within a group are treated as equivalent
  const colorAliases = [
    ['multicam', 'multicam original', 'camouflage', 'camo', 'mc'],
    ['olive', 'olive green', 'olive drab', 'od', 'od green'],
    ['coyote', 'coyote tan', 'coyote brown', 'tan', 'fde'],
    ['schwarz', 'black', 'noir'],
    ['ranger green', 'adaptive green'],
    ['grau', 'grey', 'gray', 'wolf grey', 'wolf gray'],
  ];
  const _aliasMap = {};
  colorAliases.forEach((group, i) => group.forEach(name => _aliasMap[name] = i));

  function colorMatch(a, b) {
    if (!a || !b) return false;
    const al = a.toLowerCase(), bl = b.toLowerCase();
    if (al === bl || al.includes(bl) || bl.includes(al)) return true;
    // Check alias groups
    const ga = _aliasMap[al], gb = _aliasMap[bl];
    if (ga !== undefined && ga === gb) return true;
    return false;
  }

  // Find a matching key in an object using fuzzy color matching, returns the value or undefined
  function findInMap(color, map) {
    if (!map || !color) return undefined;
    if (map[color]) return map[color];
    const lc = color.toLowerCase();
    for (const [key, val] of Object.entries(map)) {
      const kl = key.toLowerCase();
      if (kl === lc || kl.includes(lc) || lc.includes(kl)) return val;
    }
    return undefined;
  }

  // Fallback color map for tactical/common colors not in global color options
  const fallbackColorMap = {
    'multicam': '#6B5B3A',
    'multicam black': '#2A2A2A',
    'multicam tropic': '#3D5A3A',
    'multicam arid': '#C4A96A',
    'multicam alpine': '#E8E4DF',
    'ranger green': '#4A5D23',
    'od green': '#3C5A14',
    'olive green': '#556B2F',
    'adaptive green': '#4A5D23',
    'wolf grey': '#7A7A7A',
    'grey': '#808080',
    'grau': '#808080',
    'tan': '#D2B48C',
    'coyote': '#8B7355',
    'khaki': '#C3B091',
    'flecktarn': '#5A6B3C',
    'tropentarn': '#A69060',
    'navy': '#1B2838',
    'weiß': '#F0F0F0',
    'white': '#F0F0F0',
    'rot': '#8B2020',
    'red': '#8B2020',
  };

  const defaultAttachmentIds = attachments
    .filter(a => a.isDefault)
    .map(a => a.id);

  Alpine.data('configurator', () => ({
    settings,
    colors,
    sizes,
    zones,
    baseImages,
    attachments,
    presets,

    canvasWidth: settings.canvasWidth || 800,
    canvasHeight: settings.canvasHeight || 1000,
    selectedColor: colors.length > 0 ? colors[0].name : '',
    selectedSize: sizes.length > 0 ? sizes[0].label : '',
    selectedSizeValue: sizes.length > 0 ? sizes[0].value : '',
    selectedAttachments: [...defaultAttachmentIds],
    attachmentColors: {},
    activeZone: zones.length > 0 ? zones[0].id : '',
    activePreset: null,
    isAdding: false,
    addedSuccess: false,
    cartError: '',
    detailAttachment: null,

    _stage: null,
    _layer: null,
    _nodeMap: {},
    _imageCache: {},

    get zoneAttachments() {
      return this.attachments.filter(a => {
        if (a.zone !== this.activeZone) return false;
        if (a.dependsOn) {
          return this.selectedAttachments.some(sid => sid.startsWith(a.dependsOn));
        }
        return true;
      });
    },

    get selectedItems() {
      return this.attachments.filter(a => {
        if (!this.selectedAttachments.includes(a.id)) return false;
        // Exclude if dependency not met
        if (a.dependsOn && !this.selectedAttachments.some(sid => sid.startsWith(a.dependsOn))) return false;
        return true;
      });
    },

    get subtotal() {
      const attTotal = this.selectedItems.reduce((sum, item) => sum + parseInt(item.price || '0', 10), 0);
      return (parseInt(settings.baseProductPrice, 10) || 0) + attTotal;
    },

    get bundleSavings() {
      if (settings.bundleDiscountPercent <= 0) return 0;
      const bundleItems = this.selectedItems.filter(item => item.isBundleItem);
      if (bundleItems.length < 2) return 0;
      const bundleTotal = bundleItems.reduce((sum, item) => sum + parseInt(item.price || '0', 10), 0);
      return Math.round(bundleTotal * settings.bundleDiscountPercent / 100);
    },

    get totalPrice() {
      return Math.max(0, this.subtotal - this.bundleSavings);
    },

    init() {
      this.$nextTick(() => this.initCanvas());

      // Fetch base product variants if not provided by Liquid
      if (settings.baseProductHandle && (!settings.baseVariants || settings.baseVariants.length === 0)) {
        this._baseVariantsReady = fetch(`${window.Shopify?.routes?.root || '/'}products/${settings.baseProductHandle}.js`)
          .then(r => r.json())
          .then(product => {
            settings.baseVariants = product.variants.map(v => ({
              id: v.id, option1: v.option1, option2: v.option2, available: v.available,
            }));
          })
          .catch(() => {});
      } else {
        this._baseVariantsReady = Promise.resolve();
      }

      this.$watch('selectedColor', () => this.updateBaseImage());
      this.$watch('selectedAttachments', () => this.updateAttachmentLayers());
      this.$watch('attachmentColors', () => {
        this._updateAllAttachmentImages();
        this._layer && this._layer.batchDraw();
      });

      let resizeTimer;
      const resizeHandler = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => this.fitStageToContainer(), 100);
      };
      window.addEventListener('resize', resizeHandler);
      window.addEventListener('orientationchange', () => {
        setTimeout(resizeHandler, 200);
      });
    },

    // --- Per-attachment color methods ---

    getAttachmentColors(att) {
      if (!att.variants || att.variants.length === 0) return [];
      const seen = new Set();
      const result = [];
      for (const v of att.variants) {
        if (v.color && !seen.has(v.color)) {
          seen.add(v.color);
          const lc = v.color.toLowerCase();
          // Priority: Liquid-resolved hex > fuzzy global match > fallback map > gray
          let hex = v.hex;
          if (!hex) {
            const global = this.colors.find(c => colorMatch(c.name, v.color));
            hex = global ? global.value : (fallbackColorMap[lc] || '#888');
          }
          result.push({ name: v.color, value: hex, available: v.available !== false });
        }
      }
      return result;
    },

    getSelectedAttColor(attId) {
      // Explicit per-attachment color override
      if (this.attachmentColors[attId]) return this.attachmentColors[attId];
      // Try global carrier color
      const global = this.selectedColor;
      const att = this.attachments.find(a => a.id === attId);
      if (!att || !att.variants || att.variants.length === 0) return global;
      // Check if the attachment has a variant matching the global color (fuzzy)
      const hasMatch = att.variants.some(v => colorMatch(v.color, global));
      if (hasMatch) return global;
      // Fallback: closest fuzzy match or first available variant color
      return att.variants[0].color;
    },

    isAttColorActive(attId, colorName) {
      return colorMatch(this.getSelectedAttColor(attId), colorName);
    },

    // Check if the selected color variant of an attachment is sold out
    isAttSoldOut(att) {
      if (!att.variants || att.variants.length === 0) return false;
      const color = this.getSelectedAttColor(att.id);
      const match = att.variants.find(v => colorMatch(v.color, color));
      return match ? !match.available : false;
    },

    selectAttachmentColor(attId, colorName) {
      this.attachmentColors = { ...this.attachmentColors, [attId]: colorName };
      this._updateSingleAttachment(attId);
    },

    getAttachmentDisplayImage(att) {
      const color = this.getSelectedAttColor(att.id);
      return findInMap(color, att.colorImages) || att.image;
    },

    // Used by x-effect for guaranteed Alpine reactivity
    resolveDisplayImage(att) {
      // Reading reactive props explicitly so Alpine tracks them
      const _ac = this.attachmentColors;
      const _sc = this.selectedColor;
      const color = this.getSelectedAttColor(att.id);
      return findInMap(color, att.colorImages) || att.image;
    },

    // --- Canvas ---

    async initCanvas() {
      const container = this.$refs.canvasContainer;
      if (!container || typeof Konva === 'undefined') return;

      Konva.pixelRatio = Math.min(window.devicePixelRatio || 1, 2);

      const overlayImageUrls = attachments.flatMap(a =>
        a.overlayImages ? Object.values(a.overlayImages) : []
      );
      const colorImageUrls = attachments.flatMap(a =>
        a.colorImages ? Object.values(a.colorImages) : []
      );
      const allUrls = [
        ...baseImages.filter(b => b.image).map(b => b.image),
        ...attachments.filter(a => a.image).map(a => a.image),
        ...colorImageUrls,
        ...overlayImageUrls,
      ];

      const loadedImages = await this.preloadImages(allUrls);

      const firstBase = baseImages.find(b => b.color === this.selectedColor && b.image && loadedImages[b.image]);
      if (firstBase) {
        const img = loadedImages[firstBase.image];
        this.canvasWidth = img.naturalWidth || img.width;
        this.canvasHeight = img.naturalHeight || img.height;
      }

      this._stage = new Konva.Stage({
        container,
        width: container.offsetWidth,
        height: container.offsetWidth * (this.canvasHeight / this.canvasWidth),
      });

      this._layer = new Konva.Layer({ listening: false });
      this._stage.add(this._layer);

      this.fitStageToContainer();

      // Build all render items (base images at z=0, attachments at their zIndex)
      const renderItems = [];

      baseImages.forEach(base => {
        if (!base.image || !loadedImages[base.image]) return;
        const img = loadedImages[base.image];
        const natW = img.naturalWidth || img.width;
        const natH = img.naturalHeight || img.height;
        const scale = (base.scale || 100) / 100;
        renderItems.push({ type: 'base', z: 0, base, config: {
          image: img,
          x: ((base.offsetX || 0) / 100) * this.canvasWidth,
          y: ((base.offsetY || 0) / 100) * this.canvasHeight,
          width: natW * scale,
          height: natH * scale,
          visible: base.color === this.selectedColor,
          listening: false,
        }});
      });

      attachments.forEach(att => {
        const bestImage = this._getAttachmentImage(att, this.selectedColor, loadedImages);
        if (!bestImage) return;
        const x = (att.imageX / 100) * this.canvasWidth;
        const y = (att.imageY / 100) * this.canvasHeight;
        const w = att.imageW ? (att.imageW / 100) * (bestImage.naturalWidth || bestImage.width) : undefined;
        const h = att.imageH ? (att.imageH / 100) * (bestImage.naturalHeight || bestImage.height) : undefined;
        const config = { image: bestImage, x, y, visible: this.selectedAttachments.includes(att.id), listening: false };
        if (w) config.width = w;
        if (h) config.height = h;
        renderItems.push({ type: 'att', z: att.zIndex ?? 10, att, config });
      });

      // Sort by z — negative attachments render behind base images
      renderItems.sort((a, b) => a.z - b.z);

      renderItems.forEach(item => {
        const node = new Konva.Image(item.config);
        if (item.type === 'base') {
          this._nodeMap['base_' + item.base.color] = node;
        } else {
          this._nodeMap['att_' + item.att.id] = node;
        }
        this._layer.add(node);
      });

      this._layer.batchDraw();
    },

    _getRequiredCanvasHeight() {
      let maxBottom = this.canvasHeight;
      if (!this._layer) return maxBottom;
      this._layer.children.forEach(node => {
        if (!node.visible()) return;
        const bottom = node.y() + (node.height() || 0);
        if (bottom > maxBottom) maxBottom = bottom;
      });
      return maxBottom;
    },

    fitStageToContainer() {
      if (!this._stage) return;
      const container = this.$refs.canvasContainer;
      if (!container) return;

      const containerWidth = container.offsetWidth;
      const reqHeight = this._getRequiredCanvasHeight();
      const scale = containerWidth / this.canvasWidth;

      this._stage.width(this.canvasWidth * scale);
      this._stage.height(reqHeight * scale);
      this._stage.scale({ x: scale, y: scale });
      this._layer.batchDraw();
    },

    preloadImages(urls) {
      const unique = [...new Set(urls)];
      const promises = unique.map(url => {
        if (this._imageCache[url]) return Promise.resolve();
        return new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            this._imageCache[url] = img;
            resolve();
          };
          img.onerror = () => resolve();
          img.src = url;
        });
      });
      return Promise.all(promises).then(() => this._imageCache);
    },

    _getAttachmentImage(att, globalColor, cache) {
      const color = this.getSelectedAttColor(att.id);
      // 1. Per-color overlay image (editor uploaded) — fuzzy match
      const overlayUrl = findInMap(color, att.overlayImages);
      if (overlayUrl && cache[overlayUrl]) return cache[overlayUrl];
      // 2. Color-matched variant image — fuzzy match
      const colorUrl = findInMap(color, att.colorImages);
      if (colorUrl && cache[colorUrl]) return cache[colorUrl];
      // 3. Fallback to featured product image
      if (att.image && cache[att.image]) return cache[att.image];
      return null;
    },

    updateBaseImage() {
      // Lock current attachment colors before changing global color
      const frozen = { ...this.attachmentColors };
      this.attachments.forEach(att => {
        if (!frozen[att.id]) {
          frozen[att.id] = this.getSelectedAttColor(att.id);
        }
      });
      this.attachmentColors = frozen;

      baseImages.forEach(base => {
        const node = this._nodeMap['base_' + base.color];
        if (node) node.visible(base.color === this.selectedColor);
      });
      this._updateAllAttachmentImages();
      if (this._layer) this._layer.batchDraw();
    },

    _updateAllAttachmentImages() {
      attachments.forEach(att => {
        const node = this._nodeMap['att_' + att.id];
        if (!node) return;
        const newImg = this._getAttachmentImage(att, this.selectedColor, this._imageCache);
        if (newImg) node.image(newImg);
      });
    },

    _updateSingleAttachment(attId) {
      const att = this.attachments.find(a => a.id === attId);
      if (!att) return;
      const node = this._nodeMap['att_' + att.id];
      if (!node) return;
      const newImg = this._getAttachmentImage(att, this.selectedColor, this._imageCache);
      if (newImg) {
        node.image(newImg);
        this._layer.batchDraw();
      }
    },

    updateAttachmentLayers() {
      if (!this._layer) return;
      attachments.forEach(att => {
        const node = this._nodeMap['att_' + att.id];
        if (node) node.visible(this.selectedAttachments.includes(att.id));
      });
      this.fitStageToContainer();
    },

    openDetail(att) {
      this.detailAttachment = att;
      document.body.style.overflow = 'hidden';
    },

    closeDetail() {
      this.detailAttachment = null;
      document.body.style.overflow = '';
    },

    selectColor(name) {
      this.selectedColor = name;
    },

    setActiveZone(zoneId) {
      this.activeZone = zoneId;
    },

    isAttachmentSelected(id) {
      return this.selectedAttachments.includes(id);
    },

    toggleAttachment(id) {
      this.activePreset = null;
      const idx = this.selectedAttachments.indexOf(id);
      if (idx > -1) {
        // Deselecting — also deselect any attachments that depend on this one
        this.selectedAttachments.splice(idx, 1);
        const removedHandle = id.split('--')[0];
        const dependents = this.attachments.filter(a =>
          a.dependsOn && a.dependsOn === removedHandle && this.selectedAttachments.includes(a.id)
        );
        dependents.forEach(dep => {
          const depIdx = this.selectedAttachments.indexOf(dep.id);
          if (depIdx > -1) this.selectedAttachments.splice(depIdx, 1);
        });
      } else {
        // Check if zone is "only one" — deselect other attachments in same zone
        const att = this.attachments.find(a => a.id === id);
        if (att) {
          const zone = this.zones.find(z => z.id === att.zone);
          if (zone && zone.onlyOne) {
            const zoneAttIds = this.attachments.filter(a => a.zone === att.zone).map(a => a.id);
            this.selectedAttachments = this.selectedAttachments.filter(sid => !zoneAttIds.includes(sid));
          }
        }
        this.selectedAttachments.push(id);
      }
      this.selectedAttachments = [...this.selectedAttachments];
    },

    applyPreset(preset) {
      this.activePreset = preset.name;
      this.selectedAttachments = preset.attachments
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
    },

    clearPreset() {
      this.activePreset = null;
      this.selectedAttachments = [...defaultAttachmentIds];
    },

    formatPrice(cents) {
      const num = parseInt(cents, 10) || 0;
      const amount = (num / 100).toFixed(2);
      const withComma = amount.replace('.', ',');
      return moneyFormat
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
        .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, withComma)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(num / 100).toString())
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(num / 100).toString())
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    },

    async addToCart() {
      if (this.isAdding) return;
      this.isAdding = true;
      this.cartError = '';
      this.addedSuccess = false;

      // Ensure base variants are loaded
      if (this._baseVariantsReady) await this._baseVariantsReady;

      const bundleId = 'pc-' + Date.now().toString(36);
      const items = [];

      // Base product (Plate Carrier itself) — match selected color + size
      if (settings.baseVariants && settings.baseVariants.length > 0) {
        const baseMatch = settings.baseVariants.find(v =>
          colorMatch(v.option1, this.selectedColor) && v.option2 === this.selectedSize
        ) || settings.baseVariants.find(v =>
          colorMatch(v.option1, this.selectedColor)
        ) || settings.baseVariants.find(v =>
          v.option2 === this.selectedSize
        ) || settings.baseVariants[0];

        items.push({
          id: baseMatch.id,
          quantity: 1,
          properties: {
            '_bundle_id': bundleId,
            '_configurator': 'plate-carrier',
            '_zone': 'base',
            '_color': this.selectedColor,
            '_size': this.selectedSize,
          },
        });
      } else if (settings.baseVariantId) {
        items.push({
          id: parseInt(settings.baseVariantId, 10),
          quantity: 1,
          properties: {
            '_bundle_id': bundleId,
            '_configurator': 'plate-carrier',
            '_zone': 'base',
            '_color': this.selectedColor,
          },
        });
      }

      // Attachments — match color + size (same logic as base product)
      this.selectedItems.forEach(item => {
        const attColor = this.getSelectedAttColor(item.id);
        let vid = item.variantId;
        if (item.variants && item.variants.length > 0) {
          const match = item.variants.find(v =>
            colorMatch(v.color, attColor) && v.option2 && v.option2 === this.selectedSize
          ) || item.variants.find(v => colorMatch(v.color, attColor));
          if (match) vid = match.id;
        }
        if (vid) {
          items.push({
            id: parseInt(vid, 10),
            quantity: 1,
            properties: {
              '_bundle_id': bundleId,
              '_configurator': 'plate-carrier',
              '_zone': item.zone,
              '_color': attColor,
            },
          });
        }
      });

      if (items.length === 0) {
        this.cartError = 'Keine gültigen Varianten ausgewählt.';
        this.isAdding = false;
        return;
      }

      try {
        const response = await fetch(
          (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items,
              sections: 'cart-drawer,cart-icon-bubble',
              sections_url: window.location.pathname,
            }),
          }
        );

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.description || 'Fehler beim Hinzufügen zum Warenkorb.');
        }

        const result = await response.json();

        // Fetch fresh cart state for section rendering
        const cartDrawerEl = document.querySelector('cart-drawer-component');
        const cartSectionId = cartDrawerEl ? cartDrawerEl.closest('[id^="shopify-section-"]')?.id?.replace('shopify-section-', '') : null;
        const sectionsToFetch = ['cart-icon-bubble'];
        if (cartSectionId) sectionsToFetch.push(cartSectionId);

        const cartRes = await fetch(
          `${window.Shopify?.routes?.root || '/'}cart.js`,
          { headers: { 'Accept': 'application/json' } }
        );
        const cart = await cartRes.json();

        // Dispatch theme's cart:update event so all cart components refresh
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, sourceId: 'configurator', data: { source: 'configurator', sections: result.sections || {} } },
        }));

        // Open cart drawer
        if (cartDrawerEl && typeof cartDrawerEl.open === 'function') {
          cartDrawerEl.open();
        } else {
          // Fallback: try showDialog or generic cart-drawer
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
        }

        this.addedSuccess = true;
        setTimeout(() => { this.addedSuccess = false; }, 2000);
      } catch (err) {
        this.cartError = err.message || 'Ein Fehler ist aufgetreten.';
        setTimeout(() => { this.cartError = ''; }, 5000);
      } finally {
        this.isAdding = false;
      }
    },
  }));
});
